import { useState, useEffect } from "react";
import { AdminLayout } from "@/components/shared/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  getLeads, updateLead, adminCreateLead, bulkUploadLeads,
  addLeadNote, getLeadNotes, convertLeadToApplication, deleteLead,
} from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  FileText, Filter, Search, Eye, Loader2, Plus, Upload, ArrowRightCircle,
  MessageSquare, Trash2, Clock, Edit, Download,
} from "lucide-react";
import { exportToCSV } from "@/lib/exportCSV";

const downloadSampleCSV = (type) => {
  const samples = {
    leads: { filename: "sample_leads.csv", content: "name,email,phone,company,message,source\nJohn Doe,john@example.com,555-0100,Acme Corp,Interested in becoming a dealer,website\nJane Smith,jane@example.com,555-0200,Smith Wireless,Referral from partner,referral" },
  };
  const { filename, content } = samples[type];
  const blob = new Blob([content], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const statusOptions = [
  { value: "new", label: "New", color: "bg-blue-100 text-blue-700" },
  { value: "contacted", label: "Contacted", color: "bg-yellow-100 text-yellow-700" },
  { value: "qualified", label: "Qualified", color: "bg-purple-100 text-purple-700" },
  { value: "lost", label: "Lost", color: "bg-red-100 text-red-700" },
];

const emptyLead = { name: "", email: "", phone: "", company: "", message: "", source: "admin" };

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLead, setSelectedLead] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isBulkOpen, setIsBulkOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState(emptyLead);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [notesLoading, setNotesLoading] = useState(false);

  const fetchLeads = async () => {
    try {
      const data = await getLeads(statusFilter === "all" ? null : statusFilter);
      setLeads(data);
    } catch (error) {
      console.error("Error fetching leads:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLeads(); }, [statusFilter]);

  const filteredLeads = leads.filter((lead) =>
    lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (lead.company || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});

  const handleSelectLead = async (lead) => {
    setSelectedLead(lead);
    setEditMode(false);
    setEditData({ name: lead.name, email: lead.email, phone: lead.phone, company: lead.company || "", message: lead.message || "", source: lead.source || "" });
    setNotesLoading(true);
    try {
      const n = await getLeadNotes(lead.id);
      setNotes(n);
    } catch { setNotes([]); }
    setNotesLoading(false);
  };

  const handleSaveEdit = async () => {
    setUpdating(true);
    try {
      await updateLead(selectedLead.id, editData);
      toast.success("Lead updated");
      setEditMode(false);
      setSelectedLead({ ...selectedLead, ...editData });
      fetchLeads();
    } catch { toast.error("Failed to update lead"); }
    setUpdating(false);
  };

  const handleUpdateStatus = async (newStatus) => {
    if (!selectedLead) return;
    setUpdating(true);
    try {
      await updateLead(selectedLead.id, { status: newStatus });
      toast.success("Lead status updated");
      fetchLeads();
      setSelectedLead({ ...selectedLead, status: newStatus });
    } catch { toast.error("Failed to update"); }
    setUpdating(false);
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !selectedLead) return;
    try {
      const result = await addLeadNote(selectedLead.id, newNote);
      setNotes([result.note, ...notes]);
      setNewNote("");
      toast.success("Note added");
    } catch { toast.error("Failed to add note"); }
  };

  const handleConvert = async () => {
    if (!selectedLead) return;
    if (!window.confirm("Convert this lead to a dealer application?")) return;
    setUpdating(true);
    try {
      const result = await convertLeadToApplication(selectedLead.id);
      if (result.already_existed) {
        toast.success("Lead linked to existing application. View it in the Applications section.");
      } else {
        toast.success("Lead converted to application!");
      }
      setSelectedLead(null);
      fetchLeads();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to convert");
    }
    setUpdating(false);
  };

  const handleDeleteLead = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await deleteLead(id);
      toast.success("Lead deleted");
      if (selectedLead?.id === id) setSelectedLead(null);
      fetchLeads();
    } catch { toast.error("Failed to delete"); }
  };

  const handleCreateLead = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await adminCreateLead(formData);
      toast.success("Lead created");
      setIsCreateOpen(false);
      setFormData(emptyLead);
      fetchLeads();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create lead");
    }
    setSubmitting(false);
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSubmitting(true);
    try {
      const result = await bulkUploadLeads(file);
      toast.success(`Created ${result.created} leads`);
      if (result.errors.length > 0) toast.error(`${result.errors.length} errors`);
      setIsBulkOpen(false);
      fetchLeads();
    } catch { toast.error("Bulk upload failed"); }
    setSubmitting(false);
  };

  const getStatusColor = (s) => statusOptions.find((o) => o.value === s)?.color || "bg-slate-100 text-slate-700";

  return (
    <AdminLayout title="Lead Management" breadcrumb="Leads">
      <div className="space-y-6" data-testid="admin-leads-page">
        <div className="flex flex-col sm:flex-row gap-4 justify-between">
          <div className="flex gap-2 flex-wrap">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search leads..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9 w-64" data-testid="lead-search-input" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40" data-testid="lead-status-filter">
                <Filter className="w-4 h-4 mr-2" /><SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Leads</SelectItem>
                {statusOptions.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => exportToCSV(filteredLeads, [
              { label: "Name", accessor: "name" },
              { label: "Email", accessor: "email" },
              { label: "Phone", accessor: "phone" },
              { label: "Company", accessor: "company" },
              { label: "Source", accessor: "source" },
              { label: "Status", accessor: "status" },
              { label: "Message", accessor: "message" },
              { label: "Date", accessor: (r) => r.created_at ? new Date(r.created_at).toLocaleDateString() : "" },
            ], "leads")} data-testid="export-leads-btn">
              <Download className="w-4 h-4 mr-2" />Export
            </Button>
            <Button variant="outline" onClick={() => setIsBulkOpen(true)} data-testid="bulk-upload-leads-btn">
              <Upload className="w-4 h-4 mr-2" />Bulk Upload
            </Button>
            <Button onClick={() => setIsCreateOpen(true)} className="bg-teal hover:bg-teal-dark text-white" data-testid="create-lead-btn">
              <Plus className="w-4 h-4 mr-2" />New Lead
            </Button>
          </div>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-navy flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-500" />All Leads
                </CardTitle>
                <span className="text-sm text-slate-500 font-body">{filteredLeads.length} leads</span>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-slate-500 text-center py-8 font-body">Loading...</p>
              ) : filteredLeads.length === 0 ? (
                <div className="text-center py-8"><FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" /><p className="text-slate-500 font-body">No leads found</p></div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="leads-table">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">Name</th>
                        <th className="text-left py-3 px-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">Contact</th>
                        <th className="text-left py-3 px-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">Source</th>
                        <th className="text-left py-3 px-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">Date</th>
                        <th className="text-left py-3 px-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">Status</th>
                        <th className="text-right py-3 px-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredLeads.map((lead) => (
                        <tr key={lead.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                          <td className="py-3 px-2">
                            <p className="font-medium text-navy font-body">{lead.name}</p>
                            {lead.company && <p className="text-xs text-slate-500 font-body">{lead.company}</p>}
                          </td>
                          <td className="py-3 px-2">
                            <p className="text-sm text-slate-600 font-body">{lead.email}</p>
                            <p className="text-xs text-slate-500 font-body">{lead.phone}</p>
                          </td>
                          <td className="py-3 px-2"><span className="text-sm text-slate-600 font-body capitalize">{(lead.source || "").replace("_", " ")}</span></td>
                          <td className="py-3 px-2 text-sm text-slate-600 font-body">{new Date(lead.created_at).toLocaleDateString()}</td>
                          <td className="py-3 px-2"><span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(lead.status)}`}>{lead.status}</span></td>
                          <td className="py-3 px-2 text-right">
                            <div className="flex justify-end gap-1">
                              <Button variant="ghost" size="sm" onClick={() => handleSelectLead(lead)} data-testid={`view-lead-${lead.id}`}><Eye className="w-4 h-4" /></Button>
                              {lead.status !== "converted" && (
                                <Button variant="ghost" size="sm" onClick={() => { setSelectedLead(lead); handleConvert(); }} className="text-teal" title="Convert to Application" data-testid={`convert-lead-${lead.id}`}><ArrowRightCircle className="w-4 h-4" /></Button>
                              )}
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteLead(lead.id)} className="text-red-600" data-testid={`delete-lead-${lead.id}`}><Trash2 className="w-4 h-4" /></Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Lead Detail Dialog */}
        <Dialog open={!!selectedLead} onOpenChange={() => setSelectedLead(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle className="font-heading text-navy">Lead Details</DialogTitle>
                {selectedLead && !editMode && (
                  <Button variant="outline" size="sm" onClick={() => setEditMode(true)} data-testid="edit-lead-btn">
                    <Edit className="w-4 h-4 mr-1" />Edit
                  </Button>
                )}
              </div>
            </DialogHeader>
            {selectedLead && (
              <div className="space-y-5">
                {editMode ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div><Label>Name</Label><Input value={editData.name} onChange={(e) => setEditData({ ...editData, name: e.target.value })} data-testid="edit-lead-name" /></div>
                      <div><Label>Email</Label><Input value={editData.email} onChange={(e) => setEditData({ ...editData, email: e.target.value })} data-testid="edit-lead-email" /></div>
                      <div><Label>Phone</Label><Input value={editData.phone} onChange={(e) => setEditData({ ...editData, phone: e.target.value })} data-testid="edit-lead-phone" /></div>
                      <div><Label>Company</Label><Input value={editData.company} onChange={(e) => setEditData({ ...editData, company: e.target.value })} data-testid="edit-lead-company" /></div>
                    </div>
                    <div><Label>Source</Label>
                      <Select value={editData.source} onValueChange={(v) => setEditData({ ...editData, source: v })}>
                        <SelectTrigger data-testid="edit-lead-source"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="contact_form">Contact Form</SelectItem>
                          <SelectItem value="referral">Referral</SelectItem>
                          <SelectItem value="website">Website</SelectItem>
                          <SelectItem value="phone">Phone</SelectItem>
                          <SelectItem value="walk_in">Walk-in</SelectItem>
                          <SelectItem value="social_media">Social Media</SelectItem>
                          <SelectItem value="bulk_upload">Bulk Upload</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>Message</Label><Textarea value={editData.message} onChange={(e) => setEditData({ ...editData, message: e.target.value })} rows={3} data-testid="edit-lead-message" /></div>
                    <div className="flex gap-2">
                      <Button onClick={handleSaveEdit} disabled={updating} className="bg-teal hover:bg-teal-dark text-white flex-1" data-testid="save-lead-edit-btn">
                        {updating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Save Changes
                      </Button>
                      <Button variant="outline" onClick={() => setEditMode(false)}>Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label className="text-slate-500">Name</Label><p className="font-medium text-navy">{selectedLead.name}</p></div>
                    <div><Label className="text-slate-500">Company</Label><p className="font-medium text-navy">{selectedLead.company || "N/A"}</p></div>
                    <div><Label className="text-slate-500">Email</Label><p className="font-medium text-navy">{selectedLead.email}</p></div>
                    <div><Label className="text-slate-500">Phone</Label><p className="font-medium text-navy">{selectedLead.phone}</p></div>
                    <div><Label className="text-slate-500">Source</Label><p className="font-medium text-navy capitalize">{(selectedLead.source || "").replace("_", " ")}</p></div>
                    <div><Label className="text-slate-500">Date</Label><p className="font-medium text-navy">{new Date(selectedLead.created_at).toLocaleDateString()}</p></div>
                  </div>
                )}
                {!editMode && selectedLead.message && (
                  <div><Label className="text-slate-500">Message</Label><p className="text-slate-700 text-sm mt-1 p-3 bg-slate-50 rounded-lg font-body">{selectedLead.message}</p></div>
                )}
                <div className="flex gap-3">
                  <div className="flex-1">
                    <Label>Update Status</Label>
                    <Select value={selectedLead.status} onValueChange={handleUpdateStatus}>
                      <SelectTrigger data-testid="lead-status-update"><SelectValue /></SelectTrigger>
                      <SelectContent>{statusOptions.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                  {selectedLead.status !== "converted" && (
                    <div className="flex items-end">
                      <Button onClick={handleConvert} disabled={updating} className="bg-teal hover:bg-teal-dark text-white" data-testid="convert-lead-btn">
                        <ArrowRightCircle className="w-4 h-4 mr-2" />Convert
                      </Button>
                    </div>
                  )}
                </div>

                {/* Notes Section */}
                <div className="border-t pt-4">
                  <Label className="text-slate-500 flex items-center gap-1 mb-3"><MessageSquare className="w-4 h-4" /> Notes & Communication Log</Label>
                  <div className="flex gap-2 mb-3">
                    <Textarea value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Add a note..." rows={2} className="flex-1" data-testid="lead-note-input" />
                    <Button onClick={handleAddNote} className="bg-navy hover:bg-navy-dark text-white self-end" data-testid="add-lead-note-btn">Add</Button>
                  </div>
                  {notesLoading ? <p className="text-sm text-slate-400">Loading notes...</p> : (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {notes.length === 0 ? <p className="text-sm text-slate-400 font-body">No notes yet</p> : notes.map((n) => (
                        <div key={n.id} className="p-3 bg-slate-50 rounded-lg">
                          <p className="text-sm text-slate-700 font-body">{n.text}</p>
                          <div className="flex items-center gap-2 mt-1 text-xs text-slate-400">
                            <span>{n.author}</span>
                            <Clock className="w-3 h-3" />
                            <span>{new Date(n.created_at).toLocaleString()}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {updating && <div className="flex items-center justify-center py-2"><Loader2 className="w-5 h-5 animate-spin text-teal" /></div>}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Create Lead Dialog */}
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="font-heading text-navy">Create New Lead</DialogTitle></DialogHeader>
            <form onSubmit={handleCreateLead} className="space-y-4" data-testid="create-lead-form">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Name *</Label><Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="new-lead-name" /></div>
                <div><Label>Email *</Label><Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required data-testid="new-lead-email" /></div>
                <div><Label>Phone *</Label><Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required data-testid="new-lead-phone" /></div>
                <div><Label>Company</Label><Input value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} data-testid="new-lead-company" /></div>
              </div>
              <div><Label>Source</Label>
                <Select value={formData.source} onValueChange={(v) => setFormData({ ...formData, source: v })}>
                  <SelectTrigger data-testid="new-lead-source"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="referral">Referral</SelectItem>
                    <SelectItem value="website">Website</SelectItem>
                    <SelectItem value="phone">Phone</SelectItem>
                    <SelectItem value="walk_in">Walk-in</SelectItem>
                    <SelectItem value="social_media">Social Media</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Message</Label><Textarea value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} rows={3} data-testid="new-lead-message" /></div>
              <Button type="submit" disabled={submitting} className="w-full bg-teal hover:bg-teal-dark text-white" data-testid="submit-new-lead-btn">
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</> : "Create Lead"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Bulk Upload Dialog */}
        <Dialog open={isBulkOpen} onOpenChange={setIsBulkOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="font-heading text-navy">Bulk Upload Leads</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-600 font-body">Upload a CSV file with columns: <code className="bg-slate-100 px-1 rounded text-xs">name, email, phone, company, message, source</code></p>
              <Button variant="outline" size="sm" onClick={() => downloadSampleCSV("leads")} className="w-full" data-testid="download-sample-leads-csv">
                <Download className="w-4 h-4 mr-2" />Download Sample CSV
              </Button>
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
                <input type="file" accept=".csv" onChange={handleBulkUpload} className="hidden" id="bulk-lead-upload" data-testid="bulk-lead-file-input" />
                <label htmlFor="bulk-lead-upload" className="cursor-pointer">
                  {submitting ? <Loader2 className="w-8 h-8 animate-spin text-teal mx-auto" /> : (
                    <><Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" /><p className="text-sm text-slate-600 font-body">Click to upload CSV</p></>
                  )}
                </label>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
